import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector, useDispatch } from 'react-redux';
import apiClient from '../api/apiClient';
import { updateChargingFee } from '../features/inputs/inputs';

const fetchResults = async () => {
  const response = await apiClient.get('api/resultpull');
  let data = response.data;
  if (typeof data === 'string') {
    data = data.replace('NaN', '0');
    data = JSON.parse(data);
  }
  return data;
};

export function useResultPull({ enabled = true } = {}) {
  const dispatch = useDispatch();
  const chargingFee = useSelector((state) => state.inputs.value.chargingFee);

  const query = useQuery({
    queryKey: ['results'],
    queryFn: fetchResults,
    enabled,
    retry: 3,
    staleTime: 0,
  });

  const { rateMin, rateMax } = useMemo(() => {
    const arr = query.data?.user_charge_rate_array;
    if (!arr?.length) return { rateMin: 0, rateMax: 0 };
    return { rateMin: Math.min(...arr), rateMax: Math.max(...arr) };
  }, [query.data]);

  // Floor-match: last entry where charging_fee <= chargingFee
  const activeResult = useMemo(() => {
    const arr = query.data?.results_array;
    if (!arr?.length) return null;
    for (let i = arr.length - 1; i >= 0; i--) {
      if (arr[i].charging_fee <= chargingFee) return arr[i];
    }
    return arr[0];
  }, [query.data, chargingFee]);

  // Clamp chargingFee if it falls outside the current min/max
  useEffect(() => {
    if (!query.data?.results_array) return;
    if (chargingFee < rateMin) dispatch(updateChargingFee(rateMin));
    else if (chargingFee > rateMax) dispatch(updateChargingFee(rateMax));
  }, [rateMin, rateMax, chargingFee, query.data, dispatch]);

  return { ...query, rateMin, rateMax, activeResult };
}
